import { IDtvTextProps } from "@directv-digital/dds.ui.dtv-text";
import { LocalRsnApiResponse } from "@directv-digital/dds.apis.local-rsn-chnls";
import { County } from "@directv-digital/dds.apis.county-lookup";

export interface ZipcodeModuleProps {
  // Required
  id: string;
  mode: "light" | "dark";
  /** Main difference is "small" on desktop has 48px height, not 56px */
  size?: "small" | "medium";
  /** Horizontal alignment of the module inside its containing box */
  position?: "left" | "center" | "right";
  /** Callback sent to parent when the search button is clicked */
  onSearchButtonClick?: (zipcode: string, counties: County[] | null, rsnFee?: string, localRsnChnls?: LocalRsnApiResponse | null, county?: County) => void;
  /** Callback sent to parent when either
   * (a) Update button is clicked and RSN Fee prefetch is completed, or
   * (b) RSN Fee fetch completed (after the search button is clicked) */
  onRsnFeeConfirmed?: (fee?: string) => void;
  /** Callback sent to parent when the RSN Fee API gives bad response */
  onRsnFeeApiError?: (error?: string) => void;
  /** Callback sent to parent when either
   * (a) Update button is clicked and RSN Fee prefetch is completed, or
   * (b) RSN Fee fetch completed (after the search button is clicked) */
  onLocalRsnChnlsConfirmed?: (channels: LocalRsnApiResponse | null, dma?: string, nielsenDma?: string) => void | null;
  /** Callback sent to parent when Local/Rsn Channels API gives bad response */
  onLocalRsnChnlsApiError?: (error?: string) => void;
  /** Callback sent to parent when either
   * (a) Update button is clicked and RSN Fee prefetch is completed, or
   * (b) RSN Fee fetch completed (after the search button is clicked) */
  onLocalRsnChnlApiLoading?: (isLoading: boolean) => void;
  /** Callback sent to parent when the cache is confirmed with zipcode and county data */
  onCacheConfirmed?: (zipcode: string, counties: County[] | null, rsnFee?: string, localRsnChnls?: LocalRsnApiResponse | null, county?: County) => void;
  /** Callback sent to parent when the Zipcode Lookup (County) API gives bad response */
  onCountyApiError?: (error?: string) => void;
  /** Search for satellite or stream local/rsn channels */
  localRsnApiOptions?: {
    satellite?: boolean;
    stream?: boolean;
  };
  /** !! Provide below if component is a secondary inpage component
   *  - Data should be retrieved from a Zipcode Module higher in DOM tree
   *  - Once enhanced context done, can be removed */
  initData?: {
    zipcode?: string;
    counties?: County[] | null;
    county?: County | null;
    rsnFee?: string;
    localRsnChnls?: LocalRsnApiResponse | null;
  };
  isConditional?: boolean;
  /** Optional component name string to differentiate the payload to identify rsn fee for regular & with agent compare tool channel */
  componentType?: string;
  // --- start -- CI Values -------------------
  ciConfig?: {
    /** Bolded header text, before the [ZipCode], [County]. Defaults to "Showing channels for ZIP Code" */
    displayText?: string;
    /** [Optional] Tooltip next to display header */
    displayTooltip?: IDtvTextProps["tooltip"]
    /** Error helper message when search button is clicked with a partial zipcode */
    zipCodeInputErrorMsg?: string;
    /** Error helper message when the County Lookup API returns no results */
    zipCodeLookupErrorMsg?: string;
    /** Error helper message when search button is clicked with no county selection */
    countySelectionErrorMsg?: string;
    /** Helper message when search button is clicked with a partial zipcode */
    countyApiHangingMsg?: string;
    /** Error display text when the Zipcode Lookup API gives bad response */
    countyApiErrorMsg?: string;
    /** Label for button to open the drawer. Defaults to "Change" */
    drawerOpenText?: string;
    /** Label for button to close the drawer. Defaults to "Hide" */
    drawerCloseText?: string;
    /** Label/placeholder for the ZIP Code field. Defaults to "Zip Code" */
    zipCodeLabel?: string;
    /** Label/placeholder for the ZIP Code field. Defaults to "Select a County" */
    countyLabel?: string;
    /** Label for the Update trigger button. Defaults to "Update Location" */
    ctaLabel?: string;
  }
  // -- end -- CI Values
}

export const defaultCiConfig = {
  displayText: "Showing channels for ",
  displayTooltip: {},
  zipCodeInputErrorMsg: "Enter a ZIP Code to continue",
  zipCodeLookupErrorMsg: "Having trouble finding that ZIP Code",
  countySelectionErrorMsg: "You must select a county to continue",
  countyApiHangingMsg: "Sorry, this search is taking a while...",
  countyApiErrorMsg: "We are having trouble showing local channel data, Please try again later",
  drawerOpenText: "Change",
  drawerCloseText: "Hide",
  zipCodeLabel: "ZIP Code",
  countyLabel: "Select your county",
  ctaLabel: "Update location"
}
